import React, { useEffect, useRef } from 'react';
import { Flex } from '@withjoy/joykit';
import ReEngagementPopupItem from './components/ReEngagementPopupItem';
import useReEngagementPopup from './hooks/useReEngagementPopup';
import { useIntercom } from '@shared/core/intercom';
import { useDashboardTelemetry } from '../../../Dashboard.telemetry';

type ReEngagementPopupProps = {
  isMobile: boolean;
  eventId?: string;
  eventHandle: string;
};

const ReEngagementPopup = ({ isMobile, eventId, eventHandle }: ReEngagementPopupProps) => {
  const { items, canRender, handleRemove } = useReEngagementPopup({ eventId, eventHandle });
  const hasTriggeredPopupViewedEvent = useRef<boolean>(false);
  const { reEngagementPopup } = useDashboardTelemetry();
  const intercom = useIntercom();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // directly using intercom.hideLauncher() & intercom.showLauncher() instead of useToggleIntercomLauncher hook since,
  // using the hook doesn't work as expected as there is a race condition already present in the dashboard
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (canRender && isMobile) {
        intercom.hideLauncher();
      }
      if (!canRender || !isMobile) {
        intercom.showLauncher();
      }
    }, 1000);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [canRender, intercom, isMobile]);

  useEffect(() => {
    if (canRender && !hasTriggeredPopupViewedEvent.current) {
      reEngagementPopup.enter();
      hasTriggeredPopupViewedEvent.current = true;
    }
  }, [canRender, reEngagementPopup]);

  if (!canRender && eventId) {
    return null;
  }

  const renderItems = () => {
    return items.map(item => {
      return item.canShow ? (
        <ReEngagementPopupItem
          key={item.key}
          itemKey={item.key}
          title={item.title}
          subtitle={item.subtitle}
          imageUrl={item.imageUrl}
          onClick={item.onClick}
          onRemove={() => handleRemove(item.key)}
        />
      ) : null;
    });
  };

  if (isMobile) {
    return (
      <Flex
        position="fixed"
        flexDirection="column"
        alignItems="stretch"
        bottom={'0px'}
        left={0}
        right={0}
        zIndex="popover"
        boxShadow="0px -4px 12px -2px rgba(44, 41, 37, 0.25), 0px -3px 7px -3px rgba(0, 0, 0, 0.30)"
        borderTopLeftRadius={3}
        borderTopRightRadius={3}
        paddingX={5}
        paddingTop={6}
        paddingBottom={7}
        gap={5}
        backgroundColor="mono14"
      >
        {renderItems()}
      </Flex>
    );
  }

  return (
    <Flex
      position="fixed"
      flexDirection="column"
      alignItems="stretch"
      top={7}
      right={7}
      width={'387px'}
      zIndex="popover"
      borderRadius={3}
      padding={5}
      gap={5}
      backgroundColor="mono14"
    >
      {renderItems()}
    </Flex>
  );
};

export default ReEngagementPopup;
